import { acceptInvitation, getInvitationByCode } from "@/api/invitations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/auth/useAuth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { trackUserSignedUp } from "@/lib/analytics";
import { parseAuthError } from "@/utils/auth-errors";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router";

interface FormData {
    newPassword: string;
    confirmNewPassword: string;
    firstName: string;
    lastName: string;
}

const CreateAccount = () => {
    const { session, authenticated, user } = useAuth();
    const navigate = useNavigate();
    const { invitationCode } = useParams();

    if (!invitationCode) {
        throw new Error("Valid code is required");
    }

    const { data } = useSuspenseQuery({
        queryKey: ["invitation", invitationCode],
        queryFn: () => getInvitationByCode(invitationCode),
        retry: false,
    });

    // Defaults seed from the invitation payload, not the auth user — the
    // create-account page runs pre-acceptance, so `user` from `useAuth()` is
    // null. Admin-invited users already have first/last names server-side; we
    // surface them as form defaults so the user only has to confirm + set a
    // password. Path B (placeholder) users fall back to "" and must fill the
    // fields themselves.
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<FormData>({
        defaultValues: {
            newPassword: "",
            confirmNewPassword: "",
            firstName: data.user?.first_name ?? "",
            lastName: data.user?.last_name ?? "",
        },
    });

    // Post-acceptance redirect target. Set by `onSuccess`; the navigate
    // itself is deferred to the useEffect below because `ProtectedRoute`
    // reads `authenticated` from `AuthProvider`, which does not flip to
    // `true` until `profileQuery.refetch()` (kicked off inside
    // `session.create`) resolves. Navigating from `onSuccess` directly
    // races ahead of that: `/organizations/...` hits `ProtectedRoute`
    // while `authenticated` is still `false`, and the guard redirects
    // to `/login` — the Path B deep-link silently evaporates. Gating on
    // `authenticated && user` mirrors the Login page's own post-submit
    // redirect pattern.
    const [pendingRedirect, setPendingRedirect] = useState<string | null>(null);

    const { mutate, isPending, error } = useMutation({
        mutationFn: ({
            id,
            new_password,
            first_name,
            last_name,
        }: {
            id: number;
            new_password: string;
            first_name: string;
            last_name: string;
        }) =>
            acceptInvitation(id, {
                new_password,
                first_name,
                last_name,
            }),
        onSuccess: (result) => {
            const redirectId = result.shared_conversation_id;
            trackUserSignedUp({
                invitation_id: data.id,
                is_shared_conversation_invite: redirectId != null,
            });
            setPendingRedirect(
                redirectId != null ? `/organizations/chat/${redirectId}` : "/"
            );
            session.create(result.access_token, result.refresh_token);
        },
    });

    const serverError = useMemo(
        () => (error ? parseAuthError(error) : null),
        [error]
    );

    const newPassword = watch("newPassword") || "";
    const firstName = data.user.first_name?.trim() || "";
    const organizationName = data.organization?.name;
    const email = data.user.email;
    const accepted = !!data.accepted_at;

    useDocumentTitle("Create account");

    useEffect(() => {
        if (!authenticated || !user) return;
        if (pendingRedirect !== null) {
            // Post-acceptance: use the resolver's chosen destination
            // (Path B deep-link, or "/" for a no-share acceptance).
            // Overrides the `is_admin` branch so an admin accepting a
            // Path B invite lands on the shared conversation rather than
            // `/admin`.
            navigate(pendingRedirect);
        } else {
            // Bootstrap: user landed here already authenticated (no
            // acceptance flow in progress — they clicked the invite link
            // but were already signed in from another tab).
            navigate(user.is_admin ? "/admin" : "/");
        }
    }, [authenticated, navigate, user, pendingRedirect]);

    if (accepted) {
        return (
            <div className="space-y-7">
                <div>
                    <p className="text-muted-foreground mb-2.5 text-[11px] font-medium tracking-[0.22em] uppercase">
                        Already used
                    </p>
                    <div className="space-y-2">
                        <h1 className="text-3xl font-semibold tracking-tight">
                            You're already set up
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            This invitation has been used. Sign in with your
                            existing password to continue.
                        </p>
                    </div>
                </div>
                <Button className="h-10 w-full" render={<Link to="/login" />}>
                    Go to sign in
                </Button>
            </div>
        );
    }

    const headline = firstName
        ? `Welcome, ${firstName}`
        : "Welcome to InsureScope";

    const onSubmit = (formData: FormData) => {
        mutate({
            id: data.id,
            new_password: formData.newPassword,
            first_name: formData.firstName,
            last_name: formData.lastName,
        });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="space-y-7">
                {/* Header */}
                <div>
                    <p className="text-muted-foreground mb-2.5 text-[11px] font-medium tracking-[0.22em] uppercase">
                        Invited
                    </p>
                    <div className="space-y-2">
                        <h1 className="text-3xl font-semibold tracking-tight">
                            {headline}
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {organizationName ? (
                                <>
                                    Set a password to join{" "}
                                    <span className="text-foreground font-medium">
                                        {organizationName}
                                    </span>{" "}
                                    on InsureScope.
                                </>
                            ) : (
                                <>
                                    Set a password to finish setting up your
                                    account.
                                </>
                            )}
                        </p>
                    </div>
                </div>

                {/* Server error */}
                {serverError && (
                    <div
                        role="alert"
                        aria-live="polite"
                        className={
                            serverError.snackBarVariant === "warning"
                                ? "border-destructive/25 bg-destructive/[0.04] text-foreground rounded-md border px-3 py-2.5 text-sm"
                                : "border-destructive/30 bg-destructive/[0.06] text-destructive rounded-md border px-3 py-2.5 text-sm"
                        }
                    >
                        {serverError.message}
                    </div>
                )}

                {/* Fields */}
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            value={email}
                            readOnly
                            className="bg-muted/50 focus-visible:border-input h-10 focus-visible:ring-0"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="first-name">First Name</Label>
                            <Input
                                id="first-name"
                                autoFocus
                                aria-invalid={!!errors.firstName}
                                {...register("firstName", {
                                    required: "First name is required",
                                })}
                            />
                            {errors.firstName && (
                                <p className="text-destructive text-sm">
                                    {errors.firstName.message}
                                </p>
                            )}
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="last-name">Last Name</Label>
                            <Input
                                id="last-name"
                                aria-invalid={!!errors.lastName}
                                {...register("lastName", {
                                    required: "Last name is required",
                                })}
                            />
                            {errors.lastName && (
                                <p className="text-destructive text-sm">
                                    {errors.lastName.message}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="newPassword">Password</Label>
                        <PasswordInput
                            id="newPassword"
                            autoComplete="new-password"
                            aria-invalid={!!errors.newPassword}
                            {...register("newPassword", {
                                required: "Password is required",
                            })}
                        />
                        {errors.newPassword && (
                            <p className="text-destructive text-sm">
                                {errors.newPassword.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="confirmNewPassword">
                            Confirm password
                        </Label>
                        <PasswordInput
                            id="confirmNewPassword"
                            autoComplete="new-password"
                            aria-invalid={!!errors.confirmNewPassword}
                            {...register("confirmNewPassword", {
                                required: "Please confirm your password",
                                validate: (value) =>
                                    value === newPassword ||
                                    "Passwords do not match",
                            })}
                        />
                        {errors.confirmNewPassword && (
                            <p className="text-destructive text-sm">
                                {errors.confirmNewPassword.message}
                            </p>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="space-y-4">
                    <Button
                        type="submit"
                        disabled={isPending}
                        className="h-10 w-full"
                    >
                        {isPending && <Spinner className="mr-2" />}
                        Create account
                    </Button>
                    <p className="text-muted-foreground text-xs leading-relaxed">
                        By creating your account, you agree to InsureScope's{" "}
                        <a
                            href="https://www.insurescope.ai/privacy-policy"
                            target="_blank"
                            rel="noreferrer"
                            className="text-foreground underline-offset-4 hover:underline"
                        >
                            privacy policy
                        </a>{" "}
                        and{" "}
                        <a
                            href="https://www.insurescope.ai/terms-of-service"
                            target="_blank"
                            rel="noreferrer"
                            className="text-foreground underline-offset-4 hover:underline"
                        >
                            terms of service
                        </a>
                        .
                    </p>
                </div>
            </div>
        </form>
    );
};

export default CreateAccount;
